/**
 * CustomerFormPanel — customer create/edit form (quote-builder embed).
 *
 * ── PR-73 Commit 2 (2026-08-24) — is_job_site provenance ──────────────
 *
 * Multi-address now carries an ADDITIONAL designation: is_job_site.
 * Provenance note for anyone greping for R-71 or R-75:
 *
 *   R-71 and R-75 (register 08-19) originally ruled that the job-site
 *   address is stored at quote/job level ONLY, with no customer-record
 *   bit. Dispatch 431 §2 (operator supersession, 2026-08-24) reversed
 *   that: is_job_site moves ONTO customer.addresses[] as a customer-
 *   level designation, alongside job.job_site_address which remains the
 *   job-level snapshot.
 *
 *   Reason for the supersession is on record in dispatch 431. If a
 *   stakeholder asks "why is job-site on the customer record when
 *   R-75 said it wasn't," the answer is here.
 *
 *   Stakeholders were not told about the supersession at the time —
 *   flagged 🟠 for next UAT (dispatch 431 §2). Presentation (Reading C,
 *   checkbox + selector) is the operator's derivation, flagged §8.11;
 *   expect it to be corrected at UAT.
 *
 *   Twin: AddCustomerDrawer.js. Both sites carry the same UI per PR-72
 *   §1.22 "fix the pattern, not one instance" discipline.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import NameFields from '../ui/NameFields';
import { AddressComposite, SelectMenu, TypeaheadChips } from '../ui';
import Checkbox from '../ui/Checkbox';
import { useToast } from '../ui/Toast';
import { customerAPI } from '../../services/api';
import { useSettingsContext } from '../../hooks/useSettings';
import useSalespersonOptions from '../../hooks/useSalespersonOptions';
import { deactivatedChipOptions, storedSalespersons } from '../../utils/userStatus';
import useTelemetry from '../../hooks/useTelemetry';
import { formatPhoneInput } from '../../utils/phone';
import { toServiceAddressShape } from '../../utils/customerAddress';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// IDI-314 followup (2026-09-12): same bug as AddCustomerDrawer.js, same file
// pair. default_margin is a documented dual-format field (some customer
// records store 0..1 decimal, this input is percentage-scale 0-99 step=0.5).
// Raw pass-through of a decimal-scale value (e.g. 0.15) fails native step
// validation and the Save button (a real in-DOM <form> submit here, unlike
// the drawer's external form="" association — but the SAME native
// checkValidity() gate applies to any submit, in-DOM or not) silently
// no-ops. This surface (CustomerBlock → CustomerFormPanel, mounted in the
// quote builder's customer block) is what the operator's review caught as
// a live, actively-used surface that a content grep for the exported name
// missed (CustomerBlock.js imports it under the alias `CustomerForm`).
function marginToDisplayScale(raw) {
  if (raw == null || raw === '') return '';
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  if (!Number.isFinite(n) || n < 0) return '';
  const pct = n > 0 && n <= 1 ? n * 100 : n;
  return String(pct);
}

// Inverse, for write-back — always emits decimal scale (0..1), matching
// AddCustomerDrawer.js's twin and the dominant existing storage convention.
function marginFromDisplayScale(display) {
  if (display === '' || display == null) return undefined;
  const n = parseFloat(display);
  if (!Number.isFinite(n)) return undefined;
  return n / 100;
}

/**
 * CustomerFormPanel — full CU-1 parity, in-place expand/collapse.
 *
 * (Historically named `CustomerForm` in the PR-3 plan. Renamed to
 * `CustomerFormPanel` because a legacy page-level `CustomerForm.js` occupies
 * that name at the `/customers/:id/edit` route. Retiring that legacy component
 * is a separate follow-up; the drawer-consolidation follow-up may reclaim the
 * shorter name at that time.)
 *
 * Used by:
 *   • CustomerBlock (quote builder) — the primary consumer this PR ships for.
 *   • Future: AddCustomerDrawer retirement will consolidate onto this.
 *
 * Props:
 *   initialData  — customer record for edit mode; null/undefined for add mode.
 *   onSave       — async (savedCustomer) => void. Called with the API response
 *                  after a successful POST /v1/customers or PUT /v1/customers/:id.
 *   onCancel     — () => void. Called when the user hits Cancel.
 *   defaultStatus — 'lead' | 'active'. Only used in add mode. Defaults to 'active' (PR-14).
 *   busy         — externally-controlled disable (parent may want to gate).
 *
 * Contract highlights (PR-3):
 *   Q1 — Customer margin field (`default_margin`) is rendered in the Terms
 *        group. Placeholder helper reads the LIVE Settings default so it
 *        stays honest if Settings changes.
 *   Q2 — Every enumerated droplist sources from `useSettings().getOptions(...)`:
 *        customer_type, payment_terms, payment_type, lead_source. No hard-coded
 *        arrays. Contact role is the recorded gap — no Settings area exists yet.
 *   Q3 — Address direction is billing-canonical. Enforced in the quote layer
 *        via resolveQuoteDisplay; the form persists `billing_same_as_primary`
 *        as usual.
 *
 * Payload shape MUST match AddCustomerDrawer.handleSubmit — a customer created
 * here and one created via the drawer must be indistinguishable server-side.
 */

// ─── Contact-role seed (PR-B2b-part-1 2026-07-21) ──────────────────────────
// Retired: the hard-coded CONTACT_ROLE_OPTIONS is gone. Contact roles are now
// managed via Settings → Customer Types & Contact Roles (see
// components/settings/CustomerTypesPage.js) and sourced through
// `useSettings.getOptions('contact_roles')`. Canonical seed values live in
// `constants/vocabSeeds.js` under `contact_role.v1`. See plan §2 PR-B2b.

const emptyContact = () => ({
  role: '',
  customer_name: '',
  phone: '',
  email: '',
  is_primary: false,
});

const emptyAddress = (isPrimary = false) => ({
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  is_primary: isPrimary,
  // PR-73 Commit 2 (dispatch 431 §3): is_job_site defaults false.
  // Under Reading C, the primary is IMPLICITLY the job-site while
  // job_site_same_as_primary is true; the bit only carries meaning
  // when the flag is false and the user has explicitly designated.
  is_job_site: false,
  label: isPrimary ? 'Primary' : 'Additional',
});

// PR-14 (2026-07-22): default status now 'active' (was 'lead').
const buildEmptyForm = (defaultStatus = 'active') => ({
  status: defaultStatus,
  first_name: '',
  last_name: '',
  customer_type: '',
  company: '',
  use_company_as_display: false,
  salesperson: [],
  contacts: [{ ...emptyContact(), is_primary: true }],
  addresses: [emptyAddress(true)],
  billing_same_as_primary: true,
  // PR-73 Commit 2 (dispatch 431 §3, Reading C).
  // See job-site checkbox handler further below and the docblock at
  // CustomerFormPanel.job-site.test.js for provenance (R-71/R-75
  // superseded by dispatch 431 §2). Twin: AddCustomerDrawer.js.
  job_site_same_as_primary: true,
  // §1 (2026-08-08): separate billing address when the checkbox is unchecked.
  // Stored as the same shape as `addresses[i]` for backend symmetry.
  billing_address: null,
  lead_source: '',
  payment_terms: '',
  payment_type: '',
  default_margin: '',
});

// Prefill helper — supports both the deployed record shape and legacy shapes.
const prefillFromCustomer = (c) => {
  if (!c) return null;
  const name = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
  const contacts = Array.isArray(c.contacts) && c.contacts.length > 0
    ? c.contacts.map((ct, i) => ({
        role: ct.role || '',
        first_name: ct.first_name || (ct.name ? ct.name.split(' ')[0] : '') || '',
        last_name: ct.last_name || (ct.name ? ct.name.split(' ').slice(1).join(' ') : '') || '',
        phone: ct.phone || '',
        email: ct.email || '',
        is_primary: ct.is_primary || (i === 0 && !c.contacts.some(x => x.is_primary)),
      }))
    : [{ ...emptyContact(), phone: c.phone || '', email: c.email || '', is_primary: true }];

  const addresses = Array.isArray(c.addresses) && c.addresses.length > 0
    ? c.addresses.map((a, i) => ({
        line1: a.line1 || '',
        line2: a.line2 || '',
        city: a.city || '',
        state: a.state || '',
        zip: a.zip || '',
        is_primary: a.is_primary || (i === 0 && !c.addresses.some(x => x.is_primary)),
        // PR-73 Commit 2: hydrate is_job_site (defensive: default false).
        // The reader-defensive contract (dispatch 429 §4) handles 0-or->=2
        // designated entries; we do not force a canonical choice here.
        is_job_site: !!a.is_job_site,
        label: a.label || (i === 0 ? 'Primary' : 'Additional'),
      }))
    : (c.address && (c.address.street || c.address.line1 || c.address.city || c.address.zip))
      // IDI-48 (2026-09-02): wire shape carries `street`/`city`/`state`/`zip` on
      // migrated customers, not `line1`. GoldenCustomerCard.js:72-99 has the canonical
      // three-branch read. Copied to the form-hydration path so the Edit drawer no
      // longer opens blank on every migrated customer (which then blanks the record
      // on save, silently). Falsifier: any of {c.address.street, c.address.line1,
      // c.address.city, c.address.zip} populated → the drawer must open with those
      // fields pre-filled.
      ? [{ ...emptyAddress(true), line1: c.address.line1 || c.address.street || '',
           line2: c.address.line2 || c.serviceAddress?.address2 || '', city: c.address.city || '',
           state: c.address.state || '', zip: c.address.zip || '' }]
      : (c.serviceAddress && (c.serviceAddress.address1 || c.serviceAddress.city || c.serviceAddress.postal))
        // IDI-48: serviceAddress uses (address1/city/state/postal) per N-14 canonical.
        ? [{ ...emptyAddress(true), line1: c.serviceAddress.address1 || '',
             line2: c.serviceAddress.address2 || '',
             city: c.serviceAddress.city || '', state: c.serviceAddress.state || '',
             zip: c.serviceAddress.postal || '' }]
        : [emptyAddress(true)];

  return {
    status: c.status || 'active',
    first_name: c.first_name || name.split(' ')[0] || '',
    last_name: c.last_name || name.split(' ').slice(1).join(' ') || '',
    customer_type: c.customer_type || c.type || '',
    company: c.company || '',
    use_company_as_display: !!c.use_company_as_display,
    // IDI-138: form.salesperson feeds TypeaheadChips's `value` prop directly,
    // which expects a flat array of raw id strings (matching options[].value)
    // -- NOT the {id, name} object shape now stored on the record (see
    // buildPayload below). Extract just the id from each entry; legacy plain-
    // string entries (old data, pre-fix) pass through unchanged since a
    // string has no `.id` to extract and IS itself the value TypeaheadChips
    // needs (even though for old records that "value" is actually a name --
    // see hooks/useSalespersonOptions.js's fallback matching for why that still
    // renders a chip rather than a blank one).
    //
    // Code review fix: do NOT fall back to `sp.name` when `sp.id` is missing
    // on an object entry -- that would silently put the display NAME back
    // into the picker's value slot for exactly this one entry, reintroducing
    // the bug this whole change exists to fix the moment the user re-saves
    // the form. A corrupt/incomplete object entry is dropped instead (the
    // chip disappears rather than reappearing wrong); the backfill tool is
    // responsible for repairing the stored record, not this read path.
    salesperson: (Array.isArray(c.salesperson) ? c.salesperson
      : Array.isArray(c.salespersons) ? c.salespersons
      : c.salesperson ? [c.salesperson] : []
    ).map((sp) => (sp && typeof sp === 'object' ? (sp.id || '') : sp)).filter(Boolean),
    contacts,
    addresses,
    billing_same_as_primary: c.billing_same_as_primary !== false,
    // PR-73 Commit 2 (dispatch 431 §3): hydrate the form-state flag.
    job_site_same_as_primary: c.job_site_same_as_primary !== false,
    // §1: hydrate separate billing address if the customer has one
    billing_address: c.billing_same_as_primary === false && c.billing_address
      ? { ...emptyAddress(false), ...c.billing_address }
      : null,
    lead_source: c.lead_source ?? c.leadSource ?? '',
    payment_terms: c.payment_terms || '',
    payment_type: c.payment_type || '',
    default_margin: marginToDisplayScale(c.default_margin ?? c.gross_margin ?? ''),
  };
};

// Build the API payload — matches AddCustomerDrawer.handleSubmit exactly so a
// customer created here is server-side indistinguishable from one created via
// the deployed drawer. Any change here must be mirrored there (and vice versa).
//
// IDI-138: `salespersonOptions` is passed in so form.salesperson (a flat array
// of ids, matching what TypeaheadChips needs for its own value/onChange
// contract) can be resolved to {id, name} pairs at the boundary where it
// actually gets written. Storing the bare id would leave the record with no
// display name if that person is later removed from the picker's options
// (deactivated, role changed); storing {id, name} means the name displayed
// on this customer is frozen at assignment time, the same way it always
// worked, but the id is now real and stable for anything that needs to
// resolve back to the actual person (e.g. "show me all of Shane's
// customers" cannot be built on a name string that a rename silently orphans).
const buildPayload = (form, salespersonOptions = []) => {
  const primaryContact = form.contacts.find(c => c.is_primary) || form.contacts[0] || {};
  // Display name: company iff toggle is on AND company is set; else first+last.
  const displayName = (form.use_company_as_display && form.company.trim())
    ? form.company.trim()
    : `${form.first_name.trim()} ${form.last_name.trim()}`.trim();

  const resolveSalesperson = (id) => {
    const opt = salespersonOptions.find((o) => o.value === id);
    return { id, name: opt?.label || id };
  };
  const salespeople = form.salesperson.map(resolveSalesperson);

  return {
    entityType: 'customer', // Iter-18 backend list-filter requirement (mirrors drawer).
    name: displayName,
    // DEC-54: first/last come directly from the split inputs. No .split(' ')
    // derivation — combining two known parts is deterministic; decomposing one
    // string into parts is a guess.
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    company: form.company.trim(),
    use_company_as_display: form.use_company_as_display,
    customer_type: form.customer_type,
    status: form.status,
    contacts: form.contacts
      .filter(c => c.first_name || c.last_name || c.phone || c.email)
      .map(c => ({
        ...c,
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      })),
    // IDI-247 (2026-09-09): the canonical stored address on the customer
    // record is `serviceAddress` — the backend read path at
    // cmd/customers/main.go:373-381 DERIVES `address` from this on GET.
    // The two previous writes here (`addresses[]` and legacy `address`
    // at :289) were either overwritten on next read or persistent-but-
    // outranking `serviceAddress` in the resolver. Dropping both and
    // writing only the canonical shape closes the latent trap. Sibling
    // AddCustomerDrawer.js:361 makes the same change.
    ...(function () {
      const primary = form.addresses.find(a => a.is_primary) || form.addresses[0];
      const shape = toServiceAddressShape(primary);
      return shape ? { serviceAddress: shape } : {};
    })(),
    billing_same_as_primary: form.billing_same_as_primary,
    // PR-73 Commit 2: the customer-level "job site same as primary" flag.
    // Snake-case body per dispatch 428 §3 (no camelCase variant reaches
    // the wire from any code path). Per-address is_job_site bits ride
    // along on addresses[] via the filter above (which now keeps
    // entries carrying line1 || zip on both surfaces, PR-73 Commit 1's
    // precondition alignment).
    job_site_same_as_primary: form.job_site_same_as_primary,
    // §1 (2026-08-08): send billing_address only when the flag says it's
    // separate AND the user actually entered something. Sending an empty
    // object would clobber a previously-saved separate address.
    billing_address: (!form.billing_same_as_primary && form.billing_address && (form.billing_address.line1 || form.billing_address.zip))
      ? form.billing_address
      : null,
    // Legacy fields for backward compat with older UIs.
    phone: primaryContact.phone || '',
    email: primaryContact.email || '',
    payment_terms: form.payment_terms || undefined,
    payment_type: form.payment_type || undefined,
    default_margin: marginFromDisplayScale(form.default_margin),
    salesperson: salespeople[0] || undefined,
    salespersons: salespeople,
    lead_source: form.status === 'lead' ? (form.lead_source || undefined) : undefined,
  };
};

// Validation — returns null on success, or a { field, message } on failure.
const validateForm = (form) => {
  if (!form.first_name.trim()) {
    return { field: 'first_name', message: 'First name is required.' };
  }
  if (!form.customer_type) {
    return { field: 'customer_type', message: 'Customer type is required.' };
  }
  const primaries = form.contacts.filter(c => c.is_primary).length;
  if (primaries !== 1) {
    return { field: 'contacts', message: 'Exactly one contact must be marked Primary.' };
  }
  for (let i = 0; i < form.contacts.length; i++) {
    const c = form.contacts[i];
    if (!c.phone && !c.email) {
      return { field: `contacts.${i}`, message: `Contact ${i + 1} needs a phone or email.` };
    }
  }
  if (form.default_margin !== '') {
    const n = parseFloat(form.default_margin);
    if (Number.isNaN(n) || n < 0 || n >= 100) {
      return { field: 'default_margin', message: 'Customer margin must be between 0 and 99.' };
    }
  }
  return null;
};

export default function CustomerForm({
  initialData = null,
  onSave,
  onCancel,
  defaultStatus = 'active',
  busy = false,
}) {
  const { getOptions } = useSettingsContext();

  // PR-B2b-part-1: contact_roles now flows through useSettings.getOptions.
  // Retired hardcoded CONTACT_ROLE_OPTIONS. Canonical values ship in vocabSeeds
  // and are merged with any tenant-added rows from Settings → Customer Types &
  // Contact Roles.
  const contactRoleOptions = getOptions('contact_roles');
  const toast = useToast();
  const { emit } = useTelemetry();
  const isEdit = !!initialData;
  const firstInputRef = useRef(null);

  const [form, setForm] = useState(() => prefillFromCustomer(initialData) || buildEmptyForm(defaultStatus));
  const [saving, setSaving] = useState(false);

  // Focus first field on mount.
  useEffect(() => {
    setTimeout(() => firstInputRef.current?.focus(), 50);
  }, []);

  // ─── Settings-sourced droplists (PR-3 Q2) ───────────────────────────────
  const customerTypeOptions = getOptions('customer_types');
  const paymentTermOptions = getOptions('payment_terms');
  const paymentTypeOptions = getOptions('payment_types');
  const leadSourceOptions = getOptions('lead_sources');

  // Salesperson typeahead — shared hook (PR follow-up 2026-07-22).
  // Retires the local state slot + useEffect + useMemo trio that was
  // duplicated verbatim with AddCustomerDrawer.js. The hook drives the
  // sellable-role filter from `vocabSeeds.user_role.v1.permissions.can_sell`
  // instead of a hardcoded 5-string allow-list — which fixes a latent bug
  // where `office_admin` users were silently excluded (SOT says they can
  // sell). See hooks/useSalespersonOptions.js for the full contract.
  const { options: offeredSalespersonOptions, deactivatedIds } = useSalespersonOptions();
  // IDI-681 D2: a deactivated salesperson already on the record isn't offered,
  // but keeps its chip by saved name (chip-only, with the Deactivated icon) and
  // buildPayload writes back that saved {id, name} unchanged.
  const salespersonOptions = useMemo(() => [
    ...(offeredSalespersonOptions || []),
    ...deactivatedChipOptions(storedSalespersons(initialData), offeredSalespersonOptions, deactivatedIds),
  ], [offeredSalespersonOptions, deactivatedIds, initialData]);

  // Live Settings default for the margin placeholder (Q1 — stays honest).
  const settingsDefaultMargin = useMemo(() => {
    // IDI-534: no hardcoded 35% fallback. A dealer who set nothing sees
    // nothing ("no margin a dealer didn't set", 10-02); null = not set.
    const marginSetting = getOptions('default_margin');
    if (Array.isArray(marginSetting) && marginSetting.length > 0) {
      const raw = marginSetting[0].value ?? marginSetting[0].label;
      const n = parseFloat(raw);
      if (!Number.isNaN(n)) return n;
    }
    return null;
  }, [getOptions]);

  // ─── Field-mutation helpers ──────────────────────────────────────────────
  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addContact = () => setForm(prev => ({
    ...prev,
    contacts: [...prev.contacts, emptyContact()],
  }));

  const removeContact = (idx) => setForm(prev => {
    if (prev.contacts.length <= 1) return prev; // never remove the last one
    const wasPrimary = prev.contacts[idx].is_primary;
    const contacts = prev.contacts.filter((_, i) => i !== idx);
    if (wasPrimary && contacts.length > 0 && !contacts.some(c => c.is_primary)) {
      contacts[0] = { ...contacts[0], is_primary: true };
    }
    return { ...prev, contacts };
  });

  const updateContact = (idx, key, val) => setForm(prev => {
    const contacts = [...prev.contacts];
    contacts[idx] = { ...contacts[idx], [key]: val };
    return { ...prev, contacts };
  });

  const setPrimaryContact = (idx) => setForm(prev => ({
    ...prev,
    contacts: prev.contacts.map((c, i) => ({ ...c, is_primary: i === idx })),
  }));

  const addAddress = () => setForm(prev => ({
    ...prev,
    addresses: [...prev.addresses, emptyAddress(false)],
  }));

  const removeAddress = (idx) => setForm(prev => {
    if (prev.addresses.length <= 1) return prev;
    const wasPrimary = prev.addresses[idx].is_primary;
    const addresses = prev.addresses.filter((_, i) => i !== idx);
    if (wasPrimary && addresses.length > 0 && !addresses.some(a => a.is_primary)) {
      addresses[0] = { ...addresses[0], is_primary: true };
    }
    // PR-73 Commit 2 (dispatch 426 §3.1): promote-on-delete for
    // is_job_site. When we collapse back to length=1, clear all
    // is_job_site bits and restore the flag (primary IS job-site
    // implicit by construction). When length remains >= 2 and the
    // flag is off with no entry designated, promote index 0.
    const collapsed = addresses.length <= 1;
    if (collapsed) {
      return {
        ...prev,
        addresses: addresses.map(a => ({ ...a, is_job_site: false })),
        job_site_same_as_primary: true,
      };
    }
    if (!addresses.some(a => a.is_job_site) && !prev.job_site_same_as_primary) {
      addresses[0] = { ...addresses[0], is_job_site: true };
    }
    return { ...prev, addresses };
  });

  const updateAddress = (idx, patch) => setForm(prev => {
    const addresses = [...prev.addresses];
    addresses[idx] = { ...addresses[idx], ...patch };
    return { ...prev, addresses };
  });

  // PR-73 Commit 2 (dispatch 431 §3, Reading C):
  // Mirrors the drawer's setJobSiteAddress. Called by the cf-job-site-
  // address radio when the user picks which of the >=2 addresses is
  // the job site.
  const setJobSiteAddress = (idx) => setForm(prev => ({
    ...prev,
    addresses: prev.addresses.map((a, i) => ({ ...a, is_job_site: i === idx })),
  }));

  // ─── Save enable/disable + submit ────────────────────────────────────────
  const validationError = useMemo(() => validateForm(form), [form]);
  const canSave = !validationError && !saving && !busy;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const err = validateForm(form);
    if (err) {
      toast.error(err.message);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(form, salespersonOptions);
      const response = isEdit
        ? await customerAPI.updateCustomer(initialData.id, payload)
        : await customerAPI.createCustomer(payload);
      // Endpoints may return { data: {...} } or the raw record.
      const savedCustomer = response?.data ?? response;
      emit(isEdit ? 'customer.updated' : 'customer.created', {
        customer_type: form.customer_type,
        status: form.status,
        via: 'customer-form',
      });
      toast.success(isEdit ? 'Customer updated' : 'Customer created');
      if (onSave) onSave(savedCustomer);
    } catch (e2) {
      toast.error(e2?.message || `Failed to ${isEdit ? 'update' : 'create'} customer.`);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 border border-[color:var(--color-border)] bg-white p-4"
      style={{ borderRadius: 'var(--radius-md)' }}
      aria-label={isEdit ? 'Edit customer' : 'Add customer'}
    >
      {/* ─── Identity group ─── */}
      <fieldset className="space-y-3">
        <div className="flex items-center justify-between">
          <legend className="text-sm font-semibold text-gray-900">Customer</legend>
          {/* Status toggle (Lead ⇄ Active) */}
          <div className="flex gap-1" role="tablist" aria-label="Customer status">
            <button
              type="button"
              role="tab"
              aria-selected={form.status === 'lead'}
              onClick={() => setField('status', 'lead')}
              className={clsx(
                'px-3 py-1 text-xs font-medium border transition-colors',
                form.status === 'lead'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-[color:var(--color-border)] text-gray-600 hover:border-[color:var(--color-border)] bg-white'
              )}
              style={{ borderRadius: 'var(--radius-md)' }}
            >
              Lead
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={form.status === 'active'}
              onClick={() => setField('status', 'active')}
              className={clsx(
                'px-3 py-1 text-xs font-medium border transition-colors',
                form.status === 'active'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-[color:var(--color-border)] text-gray-600 hover:border-[color:var(--color-border)] bg-white'
              )}
              style={{ borderRadius: 'var(--radius-md)' }}
            >
              Active
            </button>
          </div>
        </div>

        <NameFields
          firstName={form.first_name}
          lastName={form.last_name}
          onFirstNameChange={(e) => setField('first_name', e.target.value)}
          onLastNameChange={(e) => setField('last_name', e.target.value)}
          firstRef={firstInputRef}
          idPrefix="cf"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="cf-type" className="block text-xs font-medium text-gray-700 mb-1">
              Customer type <span className="text-red-500">*</span>
            </label>
            <SelectMenu
              id="cf-type"
              value={form.customer_type || null}
              onChange={(v) => setField('customer_type', v || '')}
              options={customerTypeOptions}
              placeholder="Select type…"
              width="full"
            />
          </div>
          <div>
            <label htmlFor="cf-company" className="block text-xs font-medium text-gray-700 mb-1">
              Company
            </label>
            <Input
              id="cf-company"
              value={form.company}
              onChange={(e) => setField('company', e.target.value)}
              placeholder="Company name (optional)"
            />
          </div>
        </div>

        <Checkbox
          checked={form.use_company_as_display}
          onChange={(checked) => setField('use_company_as_display', checked)}
          disabled={!form.company}
          label="Show company as display name"
        />

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Salesperson(s)
          </label>
          <TypeaheadChips
            value={form.salesperson}
            onChange={(arr) => setField('salesperson', arr)}
            options={salespersonOptions}
            placeholder="Assigned rep(s)"
            aria-label="Salesperson"
          />
        </div>
      </fieldset>

      {/* ─── Contacts group ─── */}
      <fieldset className="space-y-3">
        <div className="flex items-center justify-between">
          <legend className="text-sm font-semibold text-gray-900">Contacts</legend>
          <button
            type="button"
            onClick={addContact}
            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <PlusIcon className="h-3 w-3" /> Add contact
          </button>
        </div>
        {form.contacts.map((contact, idx) => (
          <div
            key={idx}
            className="border border-[color:var(--color-border)] p-3 space-y-2 relative"
            style={{ borderRadius: 'var(--radius-md)' }}
          >
            <div className="flex items-center justify-between">
              {/* PR-64 §1.3 (R-71/R-75): the primary-contact radio only appears when
                  there is a choice to make. With one contact, is_primary is auto-true.
                  Duplicates the pattern in AddCustomerDrawer.js; shapes differ enough
                  that extraction was ruled out (377_ Q4). Model unchanged. */}
              {form.contacts.length > 1 ? (
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="radio"
                    name="cf-primary-contact"
                    checked={contact.is_primary}
                    onChange={() => setPrimaryContact(idx)}
                    style={{ accentColor: 'var(--color-error)' }}
                    aria-label={`Set contact ${idx + 1} as primary`}
                  />
                  <span>{contact.is_primary ? 'Primary contact' : 'Set as primary'}</span>
                </label>
              ) : (
                <span />
              )}
              {form.contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeContact(idx)}
                  className="text-gray-400 hover:text-red-500 p-1"
                  aria-label={`Remove contact ${idx + 1}`}
                  disabled={contact.is_primary && form.contacts.length > 1}
                  title={contact.is_primary ? 'Reassign Primary before removing' : 'Remove contact'}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div>
              <label htmlFor={`cf-c-${idx}-role`} className="block text-[11px] font-medium text-gray-600 mb-1">
                Role
              </label>
              <SelectMenu
                id={`cf-c-${idx}-role`}
                value={contact.role || null}
                onChange={(v) => updateContact(idx, 'role', v || '')}
                options={contactRoleOptions}
                placeholder="Select role…"
                width="full"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                value={contact.first_name}
                onChange={(e) => updateContact(idx, 'first_name', e.target.value)}
                placeholder="First name"
                aria-label={`Contact ${idx + 1} first name`}
              />
              <Input
                value={contact.last_name}
                onChange={(e) => updateContact(idx, 'last_name', e.target.value)}
                placeholder="Last name"
                aria-label={`Contact ${idx + 1} last name`}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                type="tel"
                value={contact.phone}
                onChange={(e) => updateContact(idx, 'phone', formatPhoneInput(e.target.value))}
                placeholder="Phone"
                aria-label={`Contact ${idx + 1} phone`}
              />
              <Input
                type="email"
                value={contact.email}
                onChange={(e) => updateContact(idx, 'email', e.target.value)}
                placeholder="Email"
                aria-label={`Contact ${idx + 1} email`}
              />
            </div>
            {!contact.phone && !contact.email && (
              <p className="text-[11px] text-red-600">Phone or email is required.</p>
            )}
          </div>
        ))}
      </fieldset>

      {/* ─── Addresses group ─── */}
      <fieldset className="space-y-3">
        <div className="flex items-center justify-between">
          <legend className="text-sm font-semibold text-gray-900">Addresses</legend>
          <button
            type="button"
            onClick={addAddress}
            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <PlusIcon className="h-3 w-3" /> Add another address
          </button>
        </div>
        {form.addresses.map((addr, idx) => (
          <div
            key={idx}
            className="border border-[color:var(--color-border)] p-3 space-y-2 relative"
            style={{ borderRadius: 'var(--radius-md)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">
                  {addr.is_primary ? 'Primary address' : `Address ${idx + 1}`}
                </span>
                {/* PR-73 Commit 2 (dispatch 431 §3, Reading C):
                    job-site selector radio. Visible ONLY when the
                    user has unchecked "job site same as primary"
                    AND there is more than one address to choose
                    between. Same rule as the drawer's twin (which
                    also renders a primary-address radio; this panel
                    has none — addresses[0] is primary by structural
                    invariant). Test guard:
                    CustomerFormPanel.job-site.test.js
                    T-uncheck-at-2 and T-symmetry-length-2. */}
                {!form.job_site_same_as_primary && form.addresses.length > 1 && (
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="radio"
                      name="cf-job-site-address"
                      checked={!!addr.is_job_site}
                      onChange={() => setJobSiteAddress(idx)}
                      style={{ accentColor: 'var(--color-action-primary)' }}
                      aria-label={`Set address ${idx + 1} as job site`}
                    />
                    <span>{addr.is_job_site ? 'Job site' : 'Set as job site'}</span>
                  </label>
                )}
              </div>
              {form.addresses.length > 1 && !addr.is_primary && (
                <button
                  type="button"
                  onClick={() => removeAddress(idx)}
                  className="text-gray-400 hover:text-red-500 p-1"
                  aria-label={`Remove address ${idx + 1}`}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <AddressComposite
              value={addr}
              onChange={(newAddr) => updateAddress(idx, newAddr)}
              idPrefix={`cf-addr-${idx}`}
            />
          </div>
        ))}
        {/* PR-73 Commit 2 (dispatch 431 §3, Reading C):
            "Job site same as primary" checkbox.
            - CHECKED (default): the primary address IS the job site.
            - UNCHECK at length=1 → creates a fresh entry flagged
              is_job_site: true.
            - UNCHECK at length>=2 → reveals the selector radios above.
            - RECHECK: flips the flag back; does NOT delete the entry
              that was created on length=1 uncheck (§1.22 — the user's
              typed data survives). See T-symmetry-length-1 in
              CustomerFormPanel.job-site.test.js.

            Provenance: R-71 and R-75 originally ruled job-site lives
            at quote/job level ONLY. Dispatch 431 §2 superseded that on
            2026-08-24: the customer-record designation ships here
            alongside the job-level snapshot. Stakeholders were not
            told about the supersession — flagged 🟠 for next UAT.

            Twin: AddCustomerDrawer.js's identical checkbox. Both sites
            edit together per PR-72's §1.22 pattern. */}
        <Checkbox
          checked={form.job_site_same_as_primary}
          onChange={(checked) => setForm(prev => {
            if (checked) {
              return { ...prev, job_site_same_as_primary: true };
            }
            if (prev.addresses.length <= 1) {
              const updated = prev.addresses.map(a => ({ ...a, is_job_site: false }));
              updated.push({
                ...emptyAddress(false),
                is_job_site: true,
                label: 'Job site',
              });
              return { ...prev, job_site_same_as_primary: false, addresses: updated };
            }
            const anyDesignated = prev.addresses.some(a => a.is_job_site);
            const nextAddresses = anyDesignated
              ? prev.addresses
              : prev.addresses.map(a => ({ ...a, is_job_site: false }));
            return {
              ...prev,
              job_site_same_as_primary: false,
              addresses: nextAddresses,
            };
          })}
          label="Job site same as primary"
        />
        <Checkbox
          checked={form.billing_same_as_primary}
          onChange={(checked) => {
            setField('billing_same_as_primary', checked);
            // §1 (2026-08-08): reveal shape.
            // PR-73 Commit 1 (2026-08-24, dispatch 427 §2 + 429 §1):
            // SYMMETRIC preservation. Re-check no longer clears
            // billing_address. The "no orphaned save" concern is already
            // served by the submit guard in buildPayload at :184, which
            // drops billing_address when the flag is on regardless of
            // what form state carries — so the clear-on-re-check costs
            // the user their typed address and prevents nothing that
            // isn't already prevented one layer down. Preserved value
            // is form-state-only; it cannot reach the wire while the
            // flag is on.
            //
            // §1.22: a guard whose stated purpose is already served
            // elsewhere is either dead or doing something it doesn't
            // advertise. Here the second effect was destructive: the
            // user's typed billing address on subsequent uncheck.
            //
            // This code is a copy of AddCustomerDrawer's same handler.
            // If you edit here, the drawer twin at
            // AddCustomerDrawer.js:586-603 needs the same edit. The
            // T-billing-symmetry test in the corresponding *.test.js
            // in each component guards this — deviating fixes it in
            // one place and reddens the other.
            if (!checked && !form.billing_address) {
              setField('billing_address', emptyAddress(false));
            }
          }}
          label="Billing address same as above"
        />
        {/* §1 (2026-08-08): reveal the billing address block when the flag is off.
            Reuses the same AddressComposite primitive as the addresses list so the
            ZIP-first UX, state auto-fill, and validation styling are consistent. */}
        {!form.billing_same_as_primary && (
          <div className="mt-3 pl-6 border-l-2 space-y-2" style={{ borderColor: 'var(--color-border)' }}>
            <legend className="text-xs font-medium text-gray-700">Billing address</legend>
            <AddressComposite
              value={form.billing_address || emptyAddress(false)}
              onChange={(newAddr) => setField('billing_address', newAddr)}
              idPrefix="cf-billing-addr"
            />
          </div>
        )}
      </fieldset>

      {/* ─── Terms group (Additional details) ─── */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-gray-900">Additional details</legend>

        {/* Lead source — visible only when status is 'lead' */}
        {form.status === 'lead' && (
          <div>
            <label htmlFor="cf-lead-source" className="block text-xs font-medium text-gray-700 mb-1">
              Lead source
            </label>
            <SelectMenu
              id="cf-lead-source"
              value={form.lead_source || null}
              onChange={(v) => setField('lead_source', v || '')}
              options={leadSourceOptions}
              placeholder="Select source…"
              width="full"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="cf-pt" className="block text-xs font-medium text-gray-700 mb-1">
              Payment terms
            </label>
            <SelectMenu
              id="cf-pt"
              value={form.payment_terms || null}
              onChange={(v) => setField('payment_terms', v || '')}
              options={paymentTermOptions}
              placeholder="Use account default (Net 30)"
              width="full"
            />
          </div>
          <div>
            <label htmlFor="cf-ptype" className="block text-xs font-medium text-gray-700 mb-1">
              Payment type
            </label>
            <SelectMenu
              id="cf-ptype"
              value={form.payment_type || null}
              onChange={(v) => setField('payment_type', v || '')}
              options={paymentTypeOptions}
              placeholder="Not set"
              width="full"
            />
          </div>
        </div>

        <div>
          <label htmlFor="cf-margin" className="block text-xs font-medium text-gray-700 mb-1">
            Customer margin (%)
          </label>
          <Input
            id="cf-margin"
            type="number"
            min="0"
            max="99"
            step="0.5"
            value={form.default_margin}
            onChange={(e) => setField('default_margin', e.target.value)}
            placeholder={settingsDefaultMargin != null ? `Use Settings default (${settingsDefaultMargin}%)` : 'Use Settings default'}
            className="w-56"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Optional per-customer override. Empty = use Settings default{settingsDefaultMargin != null ? ` (${settingsDefaultMargin}%)` : ''}.
          </p>
        </div>
      </fieldset>

      {/* ─── Footer ─── */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave}>
          {saving
            ? (isEdit ? 'Saving…' : 'Creating…')
            : (isEdit ? 'Save changes' : 'Create customer')}
        </Button>
      </div>
    </form>
  );
}

// Test-only re-exports so LATs can hit the pure helpers directly.
export { buildPayload, validateForm, prefillFromCustomer };
